import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useReportContext } from '../../context/DetailContext';
import { useModal } from '../../context/ModalContext';
import styled from 'styled-components';
import PageTop from '../components/pageTop/PageTop';
import { DetailDataProps } from './data';
import Summary from './components/Summary';
import BasicInfo from './components/BasicInfo';
import PartiesDetail from './components/PartiesDetail';
import ItemStatus from './components/ItemStatus';
import PriceAnalysis from '../detail/components/priceAnalysis/PriceAnalysis';
import { AroundRealDealFilter, RealDealFilter } from '../detail/data';
import JurisdictionCourt from '../detail/components/jurisdictionCourt/JurisdictionCourt';
import AiRecommendation from '../detail/components/aiRecommendation/AiRecommendation';
import ExpectItemService from '../../service/expect/expect';
import Loading from '../../func/components/loading/LoadingCircle';
import ScheduledPriceAnalysis from './components/ScheduledPriceAnalysis';

interface ListProps {
  address: string;
  appraisedprice: string;
  bid: string;
  caseno: string;
  courtname?: string;
  court?: string;
  img: string;
  minprice: string;
  saledate: string;
  type: string;
  unitno: string;
  viewgray: string;
  viewgray2: string;
  b_area: number | string | null;
  l_area: number | string | null;
  iidx?: string;
  iidx2?: number;
  bidprice?: number | string | null;
  a_per?: number;
}

export default function ScheduledDetail() {
  const { number } = useParams();
  const [tIdx, setTIdx] = useState(number);

  const { modalDispatch } = useModal();
  const {
    type,
    setType,
    itemType,
    setItemType,
    setLat,
    setLng,
    setBid,
    setGeom,
    setZoning,
    setQuotaAccess,
    setRegionAccess,
  } = useReportContext();

  const [loading, setLoading] = useState(false);

  const [basicInfo, setBasicInfo] = useState<DetailDataProps | null>(null);

  const [priceAnalysisData, setPriceAnalysisData] = useState(); // 시세분석
  const [realPriceData, setRealPriceData] = useState(); // 동일물건 실거래가
  const [nearRealPriceData, setNearRealPriceData] = useState(); //주변실거래가
  const [realDealFilter, setRealDealFilter] = useState<RealDealFilter>({
    iidx: Number(tIdx) ?? 0,
    b_idx: 0,
    distance: 500,
    limit: 6,
    offset: 0,
  });
  const [aroundRealDealFilter, setAroundRealDealFilter] =
    useState<AroundRealDealFilter>({
      iidx: Number(tIdx) ?? 0,
      b_idx: 0,
      distance: 500,
      limit: 6,
      offset: 0,
    });

  const [sameItemAddr, setSameItemAddr] = useState('');
  const [sameAddressItems, setSameAddressItems] = useState<ListProps[]>([]);
  const [sameAddressItems2, setSameAddressItems2] = useState<ListProps[]>([]);
  const [nearbyItemList1, setNearbyItemList1] = useState<ListProps[]>([]);
  const [nearbyItemList2, setNearbyItemList2] = useState<ListProps[]>([]);

  const parseGeom = (geomStr: string): Array<[number, number]> => {
    if (!geomStr.startsWith('MULTIPOLYGON')) return [];

    // 괄호 안의 숫자만 추출
    const coordinates = geomStr
      .replace(/MULTIPOLYGON\(\(\(|\)\)\)/g, '') // 'MULTIPOLYGON(((' 과 ')))' 제거
      .split(',') // 각 좌표 분리
      .map((coord) => coord.trim().split(' ').map(Number) as [number, number]); // [lng, lat] 변환

    return coordinates;
  };

  const getBasicInfo = async (tIdx: string) => {
    setLoading(true);
    try {
      const res = await ExpectItemService.basicInfo(tIdx);
      setQuotaAccess(true);
      setRegionAccess(true);
      setBasicInfo(res.data[0]);
      setItemType(res.data[0].type === 'l' ? 'L' : 'H');
      setZoning('용도');
      setLat(res.data[0].mapinfo[0].lat);
      setLng(res.data[0].mapinfo[0].lng);
      const parsedGeom = parseGeom(res.data[0].mapinfo[0].geom);
      setGeom(parsedGeom);
      setLoading(false);
      // setLoading(false);
    } catch (error: any) {
      console.error('API 오류:', error);
      // }
    }
  };

  const getPrice = async (tIdx: string) => {
    try {
      const res = await ExpectItemService.Price(tIdx);
      setPriceAnalysisData(res.data);
    } catch (e) {
      console.log(e);
    }
  };

  const getRealDeal = async (data: RealDealFilter) => {
    try {
      const { iidx, distance, limit, offset } = data;
      const res = await ExpectItemService.RealDeal(
        String(iidx),
        distance,
        limit,
        offset,
      );
      setRealPriceData(res.data);
    } catch (e) {
      console.log(e);
    }
  };
  const getArroundRealDeal = async (data: RealDealFilter) => {
    try {
      const { iidx, distance, limit, offset } = data;
      const res = await ExpectItemService.ArroundRealDeal(
        String(iidx),
        distance,
        limit,
        offset,
      );
      setNearRealPriceData(res.data);
    } catch (e) {
      console.log(e);
    }
  };

  const getArroundItem = async (tIdx: string) => {
    try {
      const res = await ExpectItemService.ArroundItem(tIdx);
      setSameAddressItems(res.data.list1);
      setSameAddressItems2(res.data.list2);
      setNearbyItemList1(res.data.list3);
      setNearbyItemList2(res.data.list4);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getBasicInfo(String(tIdx));
    getArroundItem(String(tIdx));
    getPrice(String(tIdx));
  }, []);

  useEffect(() => {
    if (tIdx !== '') {
      getRealDeal(realDealFilter);
    }
  }, [realDealFilter]);

  useEffect(() => {
    if (tIdx !== '') {
      getArroundRealDeal(aroundRealDealFilter);
    }
  }, [aroundRealDealFilter]);

  return (
    <Container>
      <PageTop logo />
      <CaseNumberBox>
        <span>{basicInfo?.caseno}</span>
        <CourtName>[{basicInfo?.court}]</CourtName>
      </CaseNumberBox>

      {loading && <Loading />}
      {!loading && (
        <Inner>
          {basicInfo && (
            <>
              <Summary caseInfo={basicInfo} />

              {/* 예정물건정보 */}
              <BasicInfo data={basicInfo} />

              {/* 당사자내역 */}
              <PartiesDetail data={basicInfo} />

              {/* 물건현황 */}
              <ItemStatus type={basicInfo.item_category} />
            </>
          )}

          {/* 가격분석 */}
          {priceAnalysisData && (
            <ScheduledPriceAnalysis
              realPriceData={realPriceData}
              nearRealPriceData={nearRealPriceData}
              priceAnalysisData={priceAnalysisData}
              realDealFilter={realDealFilter}
              setRealDealFilter={setRealDealFilter}
              aroundRealDealFilter={aroundRealDealFilter}
              setAroundRealDealFilter={setAroundRealDealFilter}
              sameItemAddr={sameItemAddr}
            />
          )}

          {/*관할법원 */}
          {/* {courtInfo && <JurisdictionCourt courtInfo={courtInfo} />} */}

          {sameAddressItems.length > 0 && (
            <AiRecommendation
              title="동일 지번 진행 물건"
              data={sameAddressItems}
              status="진행"
            />
          )}
          {/*  동일 지번 낙찰 물건 */}
          {sameAddressItems2.length > 0 && (
            <AiRecommendation
              title="동일 지번 낙찰 물건"
              data={sameAddressItems2}
              status="낙찰"
            />
          )}
          {/* 인근 반경 진행 물건 */}
          {nearbyItemList1.length > 0 && (
            <AiRecommendation
              title="인근 반경 진행 물건"
              data={nearbyItemList1}
              status="진행"
            />
          )}
          {/*  인근 반경 낙찰 물건 */}
          {nearbyItemList2.length > 0 && (
            <AiRecommendation
              title="인근 반경 낙찰 물건"
              data={nearbyItemList2}
              status="낙찰"
            />
          )}
        </Inner>
      )}
    </Container>
  );
}

const Container = styled.div`
  max-width: 900px;
  min-height: 100vh;
  margin: 0 auto;
  box-shadow: 0 0 20px hsla(0, 0%, 51%, 0.15);
  position: relative;
`;
const Inner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #f0f0f0;
`;

const CaseNumberBox = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  background-color: #000;
`;
const CourtName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.64);
`;

const TabBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid rgba(112, 115, 124, 0.16);
`;
const TabButton = styled.span<{ active: boolean }>`
  flex: 1;
  padding: 10px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => (props.active ? '#222' : '#989BA2')};
  border-bottom: ${(props) => (props.active ? '2px solid #222' : 'none')};
  cursor: pointer;
`;

const ReportContainer = styled.div`
  width: 100%;
  background-color: #fff;
  font-family: Spoqa Han Sans Neo;
  position: relative;
  display: flex;
  flex-direction: column;
`;
const ReportDetail = styled.div`
  width: 100%;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
`;

//  {caseInfo && (
//               <Summary
//                 caseInfo={caseInfo}
//                 imgList={imgList}
//                 basicInfo={basicInfo}
//                 realPriceData={realPriceData}
//               />
//             )}
//             {basicInfo && <BasicInfo data={basicInfo} />}
//             {bidHistory && <BiddingHistory bidHistory={bidHistory} />}
//             {bidMethod && <BiddingMethodInfo bidMethod={bidMethod} />}
//             {leaseinfo && <AfterBidInfo leaseinformation={leaseinfo} />}
//             {bidHistory && <BiddingInfo bidHistory={bidHistory} />}
//             {landInfo &&
//               houseInfo &&
//               landUsePlan &&
//               appraisalevaluation &&
//               locationInfo && (
//                 <ItemState
//                   subHouseInfo={subHouseInfo}
//                   landInfo={landInfo}
//                   houseInfo={houseInfo}
//                   landUsePlan={landUsePlan}
//                   appraisalevaluation={appraisalevaluation}
//                   locationInfo={locationInfo}
//                   groupCategory={groupCategory}
//                 />
//               )}
//             {/* 감정가 */}
//             {pAuctionAppraisalPriceInfo && (
//               <PAuctionAppraisalPrice
//                 data={pAuctionAppraisalPriceInfo}
//                 link={link}
//               />
//             )}
//             {/* 임차인현황 */}
//             {pRenterInfo && rentNote && (
//               <PTenantStatus pRenterInfo={pRenterInfo} rentNote={rentNote} />
//             )}
//             {/* 등기부 현황 */}
//             {registerStatus && (
//               <PRegisterStatus
//                 registerStatus={registerStatus}
//                 warning={warning}
//               />
//             )}
//             {/* 가격분석 */}
//             {priceAnalysisData && (
//               <PriceAnalysis
//                 realPriceData={realPriceData}
//                 nearRealPriceData={nearRealPriceData}
//                 priceAnalysisData={priceAnalysisData}
//                 realDealFilter={realDealFilter}
//                 setRealDealFilter={setRealDealFilter}
//                 aroundRealDealFilter={aroundRealDealFilter}
//                 setAroundRealDealFilter={setAroundRealDealFilter}
//                 sameItemAddr={sameItemAddr}
//               />
//             )}
//             {/* 동일 지번 진행 물건 */}
//             {sameAddressItems.length > 0 && (
//               <AiRecommendation
//                 title="동일 지번 진행 물건"
//                 data={sameAddressItems}
//                 status="진행"
//               />
//             )}
//             {/*  동일 지번 낙찰 물건 */}
//             {sameAddressItems2.length > 0 && (
//               <AiRecommendation
//                 title="동일 지번 낙찰 물건"
//                 data={sameAddressItems2}
//                 status="낙찰"
//               />
//             )}
//             {/* 인근 반경 진행 물건 */}
//             {nearbyItemList1.length > 0 && (
//               <AiRecommendation
//                 title="인근 반경 진행 물건"
//                 data={nearbyItemList1}
//                 status="진행"
//               />
//             )}
//             {/*  인근 반경 낙찰 물건 */}
//             {nearbyItemList2.length > 0 && (
//               <AiRecommendation
//                 title="인근 반경 낙찰 물건"
//                 data={nearbyItemList2}
//                 status="낙찰"
//               />
//             )}
